﻿import { assertUnreachable } from "../../../util/assertions/assertUnreachable";
import type { Angle } from "./Angle";
import { EllipseInTransformed, type IEllipse } from "./Ellipse";
import { type ILine, LineInFullSizeImage } from "./Line";
import {
	type IPoint,
	type IPointInVisibleCanvas,
	PointInFullSizeImage,
	PointInMonitorSetup,
	PointInOverviewImage,
	PointInTransformed,
	PointInVisibleCanvas,
	PointInVisibleCanvasDrawRegion,
	PointInWindow,
	transformPointToOriginal,
} from "./Point";
import {
	type IRegion,
	type Region,
	RegionInFullSizeImage,
	RegionInOverviewImage,
	RegionInTransformed,
	RegionInVisibleCanvas,
	RegionInVisibleCanvasDrawRegion,
	RegionInWindow,
	transformRegionToOriginal,
} from "./Region";
import {
	type ISize,
	SizeInFullSizeImage,
	SizeInMonitorSetup,
	SizeInOverviewImage,
	SizeInTransformed,
	SizeInVisibleCanvas,
	SizeInVisibleCanvasDrawRegion,
	SizeInWindow,
	transformSizeToOriginal,
} from "./Size";
import type { ITransformation } from "./Transformation";
import {
	type IVector,
	transformVectorToOriginal,
	VectorInFullSizeImage,
	VectorInMonitorSetup,
	VectorInOverviewImage,
	VectorInTransformed,
	VectorInVisibleCanvas,
	VectorInVisibleCanvasDrawRegion,
	VectorInWindow,
} from "./Vector";

export interface ICoordinateSpace<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> {
	kind: string;

	createPoint(x: number, y: number): IPoint<TCoordinateSpace>;

	createVector(x: number, y: number): IVector<TCoordinateSpace>;

	createLine(from: IPoint<TCoordinateSpace>, to: IPoint<TCoordinateSpace>): ILine<TCoordinateSpace>;

	createRegion(
		topLeft: IPoint<TCoordinateSpace>,
		bottomRight: IPoint<TCoordinateSpace>,
		topLeftBoundary: IPoint<TCoordinateSpace>,
		bottomRightBoundary: IPoint<TCoordinateSpace>,
	): Region<TCoordinateSpace>;

	createSize(width: number, height: number): ISize<TCoordinateSpace>;

	createEllipse(center: IPoint<TCoordinateSpace>, size: ISize<TCoordinateSpace>, rotation: Angle);
}

export class InWindow implements ICoordinateSpace<InWindow> {
	readonly kind = "in-window" as const;

	createPoint(x: number, y: number): IPoint<InWindow> {
		return new PointInWindow(x, y);
	}

	createVector(x: number, y: number): IVector<InWindow> {
		return new VectorInWindow(x, y);
	}

	createLine(from: IPoint<InWindow>, to: IPoint<InWindow>): ILine<InWindow> {
		throw new Error("Not implemented");
	}

	createRegion(topLeft: IPoint<InWindow>, bottomRight: IPoint<InWindow>, topLeftBoundary: IPoint<InWindow>, bottomRightBoundary: IPoint<InWindow>): Region<InWindow> {
		return new RegionInWindow(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}

	createSize(width: number, height: number): ISize<InWindow> {
		return new SizeInWindow(width, height);
	}

	createEllipse(center: IPoint<InWindow>, size: ISize<InWindow>, rotation: Angle): IEllipse<InWindow> {
		throw new Error("Not implemented");
	}
}

export class InOverviewImage implements ICoordinateSpace<InOverviewImage> {
	readonly kind = "in-overview-image" as const;

	createPoint(x: number, y: number): IPoint<InOverviewImage> {
		return new PointInOverviewImage(x, y);
	}

	createVector(x: number, y: number): IVector<InOverviewImage> {
		return new VectorInOverviewImage(x, y);
	}

	createLine(from: IPoint<InOverviewImage>, to: IPoint<InOverviewImage>): ILine<InOverviewImage> {
		throw new Error("Not implemented");
	}

	createRegion(
		topLeft: IPoint<InOverviewImage>,
		bottomRight: IPoint<InOverviewImage>,
		topLeftBoundary: IPoint<InOverviewImage>,
		bottomRightBoundary: IPoint<InOverviewImage>,
	): Region<InOverviewImage> {
		return new RegionInOverviewImage(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}

	createSize(width: number, height: number): ISize<InOverviewImage> {
		return new SizeInOverviewImage(width, height);
	}

	createEllipse(center: IPoint<InOverviewImage>, size: ISize<InOverviewImage>, rotation: Angle): IEllipse<InOverviewImage> {
		throw new Error("Not implemented");
	}
}

export class InFullSizeImage implements ICoordinateSpace<InFullSizeImage> {
	readonly kind = "in-full-image" as const;

	createPoint(x: number, y: number): IPoint<InFullSizeImage> {
		return new PointInFullSizeImage(x, y);
	}

	createVector(x: number, y: number): IVector<InFullSizeImage> {
		return new VectorInFullSizeImage(x, y);
	}

	createLine(from: IPoint<InFullSizeImage>, to: IPoint<InFullSizeImage>): ILine<InFullSizeImage> {
		return new LineInFullSizeImage(from, to);
	}

	createRegion(
		topLeft: IPoint<InFullSizeImage>,
		bottomRight: IPoint<InFullSizeImage>,
		topLeftBoundary: IPoint<InFullSizeImage>,
		bottomRightBoundary: IPoint<InFullSizeImage>,
	): Region<InFullSizeImage> {
		return new RegionInFullSizeImage(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}

	createSize(width: number, height: number): ISize<InFullSizeImage> {
		return new SizeInFullSizeImage(width, height);
	}

	createEllipse(center: IPoint<InFullSizeImage>, size: ISize<InFullSizeImage>, rotation: Angle): IEllipse<InFullSizeImage> {
		throw new Error("Not implemented");
	}
}

export class InVisibleCanvas implements ICoordinateSpace<InVisibleCanvas> {
	readonly kind = "in-visible-canvas" as const;

	createPoint(x: number, y: number): IPointInVisibleCanvas {
		return new PointInVisibleCanvas(x, y);
	}

	createVector(x: number, y: number): IVector<InVisibleCanvas> {
		return new VectorInVisibleCanvas(x, y);
	}

	createLine(from: IPoint<InVisibleCanvas>, to: IPoint<InVisibleCanvas>): ILine<InVisibleCanvas> {
		throw new Error("Not implemented");
	}

	createRegion(
		topLeft: IPoint<InVisibleCanvas>,
		bottomRight: IPoint<InVisibleCanvas>,
		topLeftBoundary: IPoint<InVisibleCanvas>,
		bottomRightBoundary: IPoint<InVisibleCanvas>,
	): Region<InVisibleCanvas> {
		return new RegionInVisibleCanvas(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}

	createSize(width: number, height: number): ISize<InVisibleCanvas> {
		return new SizeInVisibleCanvas(width, height);
	}

	createEllipse(center: IPoint<InVisibleCanvas>, size: ISize<InVisibleCanvas>, rotation: Angle): IEllipse<InVisibleCanvas> {
		throw new Error("Not implemented");
	}
}

export class InVisibleCanvasDrawRegion implements ICoordinateSpace<InVisibleCanvasDrawRegion> {
	readonly kind = "in-visible-canvas-draw-region" as const;

	createPoint(x: number, y: number): IPoint<InVisibleCanvasDrawRegion> {
		return new PointInVisibleCanvasDrawRegion(x, y);
	}

	createVector(x: number, y: number): IVector<InVisibleCanvasDrawRegion> {
		return new VectorInVisibleCanvasDrawRegion(x, y);
	}

	createLine(from: IPoint<InVisibleCanvasDrawRegion>, to: IPoint<InVisibleCanvasDrawRegion>): ILine<InVisibleCanvasDrawRegion> {
		throw new Error("Not implemented");
	}

	createRegion(
		topLeft: IPoint<InVisibleCanvasDrawRegion>,
		bottomRight: IPoint<InVisibleCanvasDrawRegion>,
		topLeftBoundary: IPoint<InVisibleCanvasDrawRegion>,
		bottomRightBoundary: IPoint<InVisibleCanvasDrawRegion>,
	): Region<InVisibleCanvasDrawRegion> {
		return new RegionInVisibleCanvasDrawRegion(topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}

	createSize(width: number, height: number): ISize<InVisibleCanvasDrawRegion> {
		return new SizeInVisibleCanvasDrawRegion(width, height);
	}

	createEllipse(center: IPoint<InVisibleCanvasDrawRegion>, size: ISize<InVisibleCanvasDrawRegion>, rotation: Angle): IEllipse<InVisibleCanvasDrawRegion> {
		throw new Error("Not implemented");
	}
}

export class InMonitorSetup implements ICoordinateSpace<InMonitorSetup> {
	readonly kind = "in-monitor-setup" as const;

	createPoint(x: number, y: number): IPoint<InMonitorSetup> {
		return new PointInMonitorSetup(x, y);
	}

	createVector(x: number, y: number): IVector<InMonitorSetup> {
		return new VectorInMonitorSetup(x, y);
	}

	createLine(from: IPoint<InMonitorSetup>, to: IPoint<InMonitorSetup>): ILine<InMonitorSetup> {
		throw new Error("Not implemented");
	}

	createRegion(
		topLeft: IPoint<InMonitorSetup>,
		bottomRight: IPoint<InMonitorSetup>,
		topLeftBoundary: IPoint<InMonitorSetup>,
		bottomRightBoundary: IPoint<InMonitorSetup>,
	): Region<InMonitorSetup> {
		throw new Error("Not implemented");
	}

	createSize(width: number, height: number): ISize<InMonitorSetup> {
		return new SizeInMonitorSetup(width, height);
	}

	createEllipse(center: IPoint<InMonitorSetup>, size: ISize<InMonitorSetup>, rotation: Angle): IEllipse<InMonitorSetup> {
		throw new Error("Not implemented");
	}
}

export class InTransformed<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> implements ICoordinateSpace<InTransformed<TCoordinateSpace>> {
	readonly kind = "in-transformed" as const;
	private readonly transformPointToOriginal: (point: IPoint<InTransformed<TCoordinateSpace>>) => IPoint<TCoordinateSpace>;
	private readonly transformRegionToOriginal: (region: IRegion<InTransformed<TCoordinateSpace>>) => IRegion<TCoordinateSpace>;
	private readonly transformVectorToOriginal: (vector: IVector<InTransformed<TCoordinateSpace>>) => IVector<TCoordinateSpace>;
	private readonly transformSizeToOriginal: (size: ISize<InTransformed<TCoordinateSpace>>) => ISize<TCoordinateSpace>;

	constructor(
		public readonly originalCoordinateSpace: TCoordinateSpace,
		public readonly transformation: ITransformation<TCoordinateSpace>,
	) {
		this.transformPointToOriginal = transformPointToOriginal(this);
		this.transformRegionToOriginal = transformRegionToOriginal(this);
		this.transformVectorToOriginal = transformVectorToOriginal(this);
		this.transformSizeToOriginal = transformSizeToOriginal(this);
	}

	createLine(from: IPoint<InTransformed<TCoordinateSpace>>, to: IPoint<InTransformed<TCoordinateSpace>>): ILine<InTransformed<TCoordinateSpace>> {
		throw new Error("Not implemented");
	}

	createPoint(x: number, y: number): IPoint<InTransformed<TCoordinateSpace>> {
		return new PointInTransformed(this.originalCoordinateSpace, this.transformation, x, y);
	}

	createRegion(
		topLeft: IPoint<InTransformed<TCoordinateSpace>>,
		bottomRight: IPoint<InTransformed<TCoordinateSpace>>,
		topLeftBoundary: IPoint<InTransformed<TCoordinateSpace>>,
		bottomRightBoundary: IPoint<InTransformed<TCoordinateSpace>>,
	): Region<InTransformed<TCoordinateSpace>> {
		return new RegionInTransformed(this.originalCoordinateSpace, this.transformation, topLeft, bottomRight, topLeftBoundary, bottomRightBoundary);
	}

	createSize(width: number, height: number): ISize<InTransformed<TCoordinateSpace>> {
		return new SizeInTransformed(this.originalCoordinateSpace, this.transformation, width, height);
	}

	createVector(x: number, y: number): IVector<InTransformed<TCoordinateSpace>> {
		return new VectorInTransformed(this.originalCoordinateSpace, this.transformation, x, y);
	}

	createEllipse(center: IPoint<InTransformed<TCoordinateSpace>>, size: ISize<InTransformed<TCoordinateSpace>>, rotation: Angle): IEllipse<InTransformed<TCoordinateSpace>> {
		return new EllipseInTransformed(this.originalCoordinateSpace, this.transformation, center, size, rotation);
	}

	transformToOriginal(value: IPoint<InTransformed<TCoordinateSpace>>): IPoint<TCoordinateSpace>;
	transformToOriginal(value: IRegion<InTransformed<TCoordinateSpace>>): IRegion<TCoordinateSpace>;
	transformToOriginal(value: IVector<InTransformed<TCoordinateSpace>>): IVector<TCoordinateSpace>;
	transformToOriginal(value: ISize<InTransformed<TCoordinateSpace>>): ISize<TCoordinateSpace>;
	transformToOriginal(
		value: IPoint<InTransformed<TCoordinateSpace>> | IRegion<InTransformed<TCoordinateSpace>> | IVector<InTransformed<TCoordinateSpace>> | ISize<InTransformed<TCoordinateSpace>>,
	) {
		switch (value.type) {
			case "point":
				return this.transformPointToOriginal(value);
			case "region":
				return this.transformRegionToOriginal(value);
			case "vector":
				return this.transformVectorToOriginal(value);
			case "size":
				return this.transformSizeToOriginal(value);
			default:
				return assertUnreachable(value);
		}
	}
}

export class CoordinateSpace {
	private static _inWindow: InWindow | null = null;
	private static _inOverviewImage: InOverviewImage | null = null;
	private static _inFullSizeImage: InFullSizeImage | null = null;
	private static _inVisibleCanvas: InVisibleCanvas | null = null;
	private static _inVisibleCanvasDrawRegion: InVisibleCanvasDrawRegion | null = null;
	private static _inMonitorSetup: InMonitorSetup | null = null;

	public static get inWindow() {
		return CoordinateSpace._inWindow ?? (CoordinateSpace._inWindow = new InWindow());
	}

	public static get inOverviewImage() {
		return CoordinateSpace._inOverviewImage ?? (CoordinateSpace._inOverviewImage = new InOverviewImage());
	}

	public static get inFullSizeImage() {
		return CoordinateSpace._inFullSizeImage ?? (CoordinateSpace._inFullSizeImage = new InFullSizeImage());
	}

	public static get inVisibleCanvas() {
		return CoordinateSpace._inVisibleCanvas ?? (CoordinateSpace._inVisibleCanvas = new InVisibleCanvas());
	}

	public static get inVisibleCanvasDrawRegion() {
		return CoordinateSpace._inVisibleCanvasDrawRegion ?? (CoordinateSpace._inVisibleCanvasDrawRegion = new InVisibleCanvasDrawRegion());
	}

	public static get inMonitorSetup() {
		return CoordinateSpace._inMonitorSetup ?? (CoordinateSpace._inMonitorSetup = new InMonitorSetup());
	}

	public static inTransformed<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(
		coordinateSpace: ICoordinateSpace<TCoordinateSpace>,
		transformation: ITransformation<TCoordinateSpace>,
	) {
		return new InTransformed<TCoordinateSpace>(coordinateSpace as TCoordinateSpace, transformation);
	}
}
